import { generatePath } from 'react-router-dom'

import { assert } from '../../../utils'

import { ConnectwareError, ConnectwareErrorType } from '../../../domain'

import { getAbsoluteRoute, getContext, getRouteInternalPath } from './Config'
import {
    type AbsoluteRoutePath,
    isAbsoluteRouteOnlyPath,
    isAbsoluteRoutePathWithId,
    isAbsoluteRoutePathWithServiceAndResourceId,
    isAbsoluteRoutePathWithServiceId,
    isRelativeRoutePathWithId,
    type RouterState,
    type RouteUpdateArgs,
} from './Domain'

export const mapState = (path: AbsoluteRoutePath, resourceId: string | null, serviceId: string | null): RouterState => {
    if (resourceId === null && serviceId === null && isAbsoluteRouteOnlyPath(path)) {
        return { path, resourceId, serviceId }
    }

    if (typeof resourceId === 'string' && serviceId === null && isAbsoluteRoutePathWithId(path)) {
        return { path, resourceId, serviceId }
    }

    if (resourceId === null && typeof serviceId === 'string' && isAbsoluteRoutePathWithServiceId(path)) {
        return { path, resourceId, serviceId }
    }

    if (typeof resourceId === 'string' && typeof serviceId === 'string' && isAbsoluteRoutePathWithServiceAndResourceId(path)) {
        return { path, resourceId, serviceId }
    }

    throw new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Router state is invalid', { path, resourceId, serviceId })
}

export const mergeState = (state: RouterState, ...args: RouteUpdateArgs): RouterState => {
    if (args.length === 1) {
        return { path: args[0], resourceId: null, serviceId: null }
    }

    const [path, id] = args
    const absolutePath = isRelativeRoutePathWithId(path) ? getAbsoluteRoute(path, getContext(state.path)) : path

    if (isAbsoluteRoutePathWithId(absolutePath)) {
        return { path: absolutePath, resourceId: id, serviceId: null }
    }

    if (isAbsoluteRoutePathWithServiceId(absolutePath)) {
        return { path: absolutePath, resourceId: null, serviceId: id }
    }

    const { serviceId } = state
    assert(serviceId, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Could not find expected serviceId', { args }))

    return { path: absolutePath, serviceId, resourceId: id }
}

export const generateIntenalPath = ({ path, serviceId, resourceId }: RouterState): string =>
    generatePath(getRouteInternalPath(path), { serviceId: serviceId || undefined, resourceId: resourceId || undefined })
